import { TicketOut } from '../api/types';

export const generateBetItemId = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string> | string) => {
  let specStr = '';
  if (typeof specifiers === 'string') {
    specStr = specifiers;
  } else if (specifiers) {
    specStr = Object.entries(specifiers)
      .map(([k, v]) => `${k}=${v}`)
      .join('|');
  }
  return `${eventId}-${marketId}-${outcomeId}${specStr ? '-' + specStr : ''}`;
};

export const isTicketWon = (t: TicketOut): boolean => {
  if (!t.legs || t.legs.length === 0) return false;
  return t.legs.every(l => l.result_status === 'won' || l.result_status === 'win');
};

export const isTicketLost = (t: TicketOut): boolean => {
  if (!t.legs || t.legs.length === 0) return false;
  return t.legs.some(l => l.result_status === 'lost' || l.result_status === 'lose');
};

export const isOpenForCashout = (t: TicketOut): boolean => {
  if (t.status !== 'open' && t.status !== 'accepted') return false;
  if (!t.legs || t.legs.length === 0) return true;
  if (isTicketLost(t)) return false;
  if (isTicketWon(t)) return false;
  // Also check if all legs are resulted in some way (won, void, cancelled)
  const allResulted = t.legs.every(l => ['won', 'win', 'void', 'cancelled'].includes(l.result_status || ''));
  if (allResulted) return false;
  return true;
};

export const buildEventGroups = (bets: { eventId?: string; marketId?: string; outcomeId?: string; specifiers?: string }[]) => {
  const groupedByEvent = bets.reduce((acc, b) => {
    if (!b.eventId || !b.marketId || !b.outcomeId) return acc;
    if (!acc[b.eventId]) acc[b.eventId] = [];
    acc[b.eventId].push({
      market_id: b.marketId,
      outcome_id: b.outcomeId,
      ...(b.specifiers ? { specifiers: b.specifiers } : {}),
    });
    return acc;
  }, {} as Record<string, { market_id: string; outcome_id: string; specifiers?: string }[]>);

  // Per API docs section 6.2: Use operator "any" for all event groups in the
  // regular betting slip. "any" means each selection is an alternative for that
  // event — the backend generates one accumulator path per alternative
  // (multi-way). operator "all" (CustomBet) should only be used through a
  // dedicated Bet Builder panel that verifies provider compatibility via
  // /v1/custombet/events/{event_id}/filter first.
  const event_groups = Object.entries(groupedByEvent).map(([event_id, selections]) => {
    return { event_id, operator: 'any' as const, selections };
  });

  return event_groups;
};
